import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider, useTheme } from "@/lib/theme";
import { LangProvider } from "@/lib/lang";
import api from "@/lib/api";
import AuroraBackground from "@/components/AuroraBackground";
import PageTransition from "@/components/PageTransition";
import CookieBanner from "@/components/CookieBanner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AppShell from "@/pages/AppShell";
import Dashboard from "@/pages/Dashboard";
import History from "@/pages/History";
import ConsolidatedReports from "@/pages/ConsolidatedReports";
import ReceiptDetail from "@/pages/ReceiptDetail";
import Billing from "@/pages/Billing";
import Connections from "@/pages/Connections";
import Transactions from "@/pages/Transactions";
import HowItWorks from "@/pages/HowItWorks";
import Honnetete from "@/pages/Honnetete";
import Tarifs from "@/pages/Tarifs";
import About from "@/pages/About";
import Security from "@/pages/Security";
import FAQ from "@/pages/FAQ";
import NotFound from "@/pages/NotFound";
import CGU from "@/pages/Legal/CGU";
import Privacy from "@/pages/Legal/Privacy";
import Mentions from "@/pages/Legal/Mentions";
import SettingsLayout from "@/pages/SettingsLayout";
import AccountPage from "@/pages/AccountPage";
import ForfaitPage from "@/pages/ForfaitPage";
import InvoicesPage from "@/pages/InvoicesPage";
import HelpPage from "@/pages/HelpPage";

import "@/App.css";

function AuthCallback() {
    const location = useLocation();
    const navigate = useNavigate();
    const { setUser, refresh } = useAuth();

    useEffect(() => {
        const fragment = location.hash || "";
        const params = new URLSearchParams(fragment.replace(/^#/, ""));
        const sessionId = params.get("session_id");
        const intent = localStorage.getItem("tk_oauth_intent");

        if (!sessionId) {
            navigate("/login");
            return;
        }
        (async () => {
            try {
                if (intent === "link-google") {
                    // Link Google to currently-logged-in user (JWT auth required)
                    localStorage.removeItem("tk_oauth_intent");
                    await api.post("/auth/link-google", { session_id: sessionId });
                    await refresh?.();
                    window.history.replaceState({}, "", "/app/account");
                    navigate("/app/account?linked=google", { replace: true });
                } else {
                    const { data } = await api.post("/auth/session", { session_id: sessionId });
                    setUser(data.user);
                    window.history.replaceState({}, "", "/app");
                    navigate("/app", { replace: true });
                }
            } catch {
                localStorage.removeItem("tk_oauth_intent");
                if (intent === "link-google") {
                    navigate("/app/account?linked=error", { replace: true });
                } else {
                    navigate("/login");
                }
            }
        })();
    }, [location.hash, navigate, setUser, refresh]);

    return (
        <div className="min-h-screen flex items-center justify-center text-[#86868B]">
            Connexion sécurisée…
        </div>
    );
}

function Protected({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-[#86868B]">
                Chargement…
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function Router() {
    const location = useLocation();
    // Auth callback synchronously during render (avoids race)
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <PageTransition>
            <Routes>
                <Route path="/" element={<Navigate to="/accueil" replace />} />
                <Route path="/accueil" element={<Landing />} />
                <Route path="/comment-ca-marche" element={<HowItWorks />} />
                <Route path="/honnetete" element={<Honnetete />} />
                <Route path="/tarifs" element={<Tarifs />} />
                <Route path="/a-propos" element={<About />} />
                <Route path="/securite" element={<Security />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/cgu" element={<CGU />} />
                <Route path="/confidentialite" element={<Privacy />} />
                <Route path="/mentions-legales" element={<Mentions />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                <Route
                    path="/app"
                    element={
                        <Protected>
                            <AppShell />
                        </Protected>
                    }
                >
                    <Route index element={<Dashboard />} />
                    <Route path="transactions" element={<Transactions />} />
                    <Route path="history" element={<History />} />
                    <Route path="recaps" element={<ConsolidatedReports />} />
                    <Route path="connections" element={<Connections />} />
                    <Route path="receipt/:id" element={<ReceiptDetail />} />
                    <Route path="settings" element={<Navigate to="/app/forfait" replace />} />
                    <Route path="billing" element={<Billing />} />

                    <Route element={<SettingsLayout />}>
                        <Route path="account" element={<AccountPage />} />
                        <Route path="forfait" element={<ForfaitPage />} />
                        <Route path="invoices" element={<InvoicesPage />} />
                        <Route path="aide" element={<HelpPage />} />
                    </Route>
                </Route>

                <Route path="*" element={<NotFound />} />
            </Routes>
        </PageTransition>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <ThemeProvider>
                    <LangProvider>
                        <AuroraBackground />
                        <div style={{ position: "relative", zIndex: 1 }}>
                            <AuthProvider>
                                <Router />
                                <CookieBanner />
                                <ToasterWithTheme />
                            </AuthProvider>
                        </div>
                    </LangProvider>
                </ThemeProvider>
            </BrowserRouter>
        </div>
    );
}

function ToasterWithTheme() {
    const { theme } = useTheme();
    return <Toaster richColors position="top-center" theme={theme} duration={2200} closeButton />;
}

export default App;
